import { ReadOnlyStepProps } from "../useStepEditor";
import { Input, Title, Stack } from "@introist/react-foundation/v2";
import { useAutomationStep } from "../../../automations/hooks/useAutomationStep";
import { Block } from "../../../../components/atoms";
import { StepEditorAccordion } from "../../../workflows/routes/WorkflowEditor/StepEditorAccordion";
import styled from "styled-components";

const RecipientCard = styled.div`
  background: var(--palette-surface-subdued);
  border-radius: var(--rounding-medium);
  padding: var(--spacing-medium);
`;

const FieldMappingCard = styled.div`
  background: var(--palette-surface-subdued);
  border-radius: var(--rounding-medium);
  padding: var(--spacing-medium);
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: var(--rounding-small);
  font-size: 12px;
  font-weight: 500;
  background: ${({ $status }) => {
    switch ($status) {
      case "completed":
        return "var(--palette-success-ghosted)";
      case "declined":
      case "voided":
        return "var(--palette-danger-ghosted)";
      default:
        return "var(--palette-primary-ghosted)";
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case "completed":
        return "var(--palette-success-default)";
      case "declined":
      case "voided":
        return "var(--palette-danger-default)";
      default:
        return "var(--palette-primary-default)";
    }
  }};
`;

type ResolvedRecipient = {
  roleName: string;
  recipientId: string;
  email: string;
  name: string;
};

type ResolvedFieldMapping = {
  tabLabel: string;
  tabId: string;
  value: string;
};

export const DocuSignStepViewer = ({ stepId }: ReadOnlyStepProps) => {
  const { step } = useAutomationStep({ id: stepId });

  if (!step) return <div />;
  if (!step.resolvedData) return <div />;

  const {
    templateName,
    resolvedEmailSubject,
    resolvedRecipients,
    resolvedFieldMappings
  } = step.resolvedData as any;

  const executionData = step.executionData as any;

  return (
    <>
      <Block blended compact>
        <StepEditorAccordion
          title="Template"
          defaultOpen={false}
          description={templateName}
        >
          <StepEditorAccordion.Row label="Template">
            <Title variant="bold">{templateName || "Unknown template"}</Title>
          </StepEditorAccordion.Row>
          {resolvedEmailSubject && (
            <StepEditorAccordion.Row label="Subject">
              <Title variant="bold">{resolvedEmailSubject}</Title>
            </StepEditorAccordion.Row>
          )}
        </StepEditorAccordion>
      </Block>

      {resolvedRecipients && resolvedRecipients.length > 0 && (
        <Block blended compact>
          <StepEditorAccordion title="Recipients" defaultOpen={false}>
            <Stack vertical gap="medium">
              {resolvedRecipients.map((recipient: ResolvedRecipient) => (
                <RecipientCard key={recipient.recipientId}>
                  <Stack vertical gap="small">
                    <Title variant="bold">{recipient.roleName}</Title>
                    <Input
                      value={recipient.email}
                      size="small"
                      variant="blended"
                      readOnly
                      style={{ background: "var(--palette-surface-subdued)" }}
                    />
                    <Input
                      value={recipient.name}
                      size="small"
                      variant="blended"
                      readOnly
                      style={{ background: "var(--palette-surface-subdued)" }}
                    />
                  </Stack>
                </RecipientCard>
              ))}
            </Stack>
          </StepEditorAccordion>
        </Block>
      )}

      {resolvedFieldMappings && resolvedFieldMappings.length > 0 && (
        <Block blended compact>
          <StepEditorAccordion title="Prefill Fields" defaultOpen={false}>
            <Stack vertical gap="medium">
              {resolvedFieldMappings.map((mapping: ResolvedFieldMapping) => (
                <FieldMappingCard key={mapping.tabId}>
                  <Stack vertical gap="small">
                    <Title variant="bold">{mapping.tabLabel}</Title>
                    <Input
                      value={mapping.value}
                      size="small"
                      variant="blended"
                      readOnly
                      style={{ background: "var(--palette-surface-subdued)" }}
                    />
                  </Stack>
                </FieldMappingCard>
              ))}
            </Stack>
          </StepEditorAccordion>
        </Block>
      )}

      {executionData && (
        <Block blended compact>
          <StepEditorAccordion title="Envelope Status" defaultOpen>
            <StepEditorAccordion.Row label="Status">
              <StatusBadge $status={executionData.status || "sent"}>
                {executionData.status || "Sent"}
              </StatusBadge>
            </StepEditorAccordion.Row>
            {executionData.envelopeId && (
              <StepEditorAccordion.Row label="Envelope ID">
                <Title style={{ fontFamily: "monospace", fontSize: "12px" }}>
                  {executionData.envelopeId}
                </Title>
              </StepEditorAccordion.Row>
            )}
            {executionData.sentAt && (
              <StepEditorAccordion.Row label="Sent At">
                <Title>{new Date(executionData.sentAt).toLocaleString()}</Title>
              </StepEditorAccordion.Row>
            )}
            {executionData.completedAt && (
              <StepEditorAccordion.Row label="Completed At">
                <Title>{new Date(executionData.completedAt).toLocaleString()}</Title>
              </StepEditorAccordion.Row>
            )}
          </StepEditorAccordion>
        </Block>
      )}
    </>
  );
};
